import { z } from 'zod';
import { RatingGradeSchema, type RatingGrade } from './types.js';

/** Systemkategorien (Plan 02 Abschnitt 3.2) mit Logik im Code */
export const SYSTEM_CATEGORY_KEYS = [
  'forester',
  'owner',
  'partner',
  'donor',
  'guide',
  'educator',
  'hotel',
  'wc',
  'supplier',
  'service',
] as const;
export type SystemCategoryKey = (typeof SYSTEM_CATEGORY_KEYS)[number];

export const CONTACT_KINDS = ['person', 'organisation'] as const;
export type ContactKind = (typeof CONTACT_KINDS)[number];
export const SALUTATIONS = ['Frau', 'Herr', 'Divers', 'keine'] as const;

export const FIELD_TYPES = ['text', 'number', 'bool', 'select', 'multiselect', 'textarea', 'date'] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

/** Felddefinition einer Kategorie (JSON in contact_category_defs.fields) */
export const FieldDefSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'Schlüssel: Kleinbuchstaben, Ziffern, Unterstrich'),
  label: z.string().min(1).max(120),
  type: z.enum(FIELD_TYPES),
  lookup: z.string().max(50).optional(),
  options: z.array(z.string()).optional(),
  required: z.boolean().optional(),
  help: z.string().max(300).optional(),
  section: z.string().max(60).optional(),
  system: z.boolean().optional(),
});
export type FieldDef = z.infer<typeof FieldDefSchema>;

export const CategoryDefSchema = z.object({
  key: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'Schlüssel: Kleinbuchstaben, Ziffern, Unterstrich'),
  name: z.string().min(1).max(60),
  name_plural: z.string().min(1).max(60),
  sort: z.number().int().default(100),
  icon: z.string().max(40).nullable().optional(),
  kinds_allowed: z.array(z.enum(CONTACT_KINDS)).min(1).default(['person', 'organisation']),
  rating_enabled: z.boolean().default(true),
  fields: z.array(FieldDefSchema).default([]),
  is_active: z.boolean().default(true),
});
export type CategoryDefInput = z.infer<typeof CategoryDefSchema>;
export interface CategoryDefDto extends CategoryDefInput {
  is_system: boolean;
}

/** Attribute gegen die Felddefinition validieren (dynamisch). Gibt {value, errors}. */
export function validateAttributes(
  fields: FieldDef[],
  raw: Record<string, unknown> | null | undefined,
): { value: Record<string, unknown>; errors: Record<string, string> } {
  const value: Record<string, unknown> = {};
  const errors: Record<string, string> = {};
  const input = raw ?? {};
  for (const f of fields) {
    const v = input[f.key];
    const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    if (empty) {
      if (f.required) errors[f.key] = 'Pflichtfeld';
      continue;
    }
    switch (f.type) {
      case 'text':
      case 'textarea':
        value[f.key] = String(v);
        break;
      case 'number': {
        const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
        if (Number.isNaN(n)) errors[f.key] = 'Keine Zahl';
        else value[f.key] = n;
        break;
      }
      case 'bool':
        value[f.key] = v === true || v === 'true' || v === 1 || v === '1' || v === 'ja';
        break;
      case 'date':
        if (!/^\d{4}-\d{2}-\d{2}$/.test(String(v))) errors[f.key] = 'Datum im Format JJJJ-MM-TT';
        else value[f.key] = String(v);
        break;
      case 'select':
        value[f.key] = String(v);
        break;
      case 'multiselect':
        value[f.key] = Array.isArray(v) ? v.map(String) : String(v).split(',').map((s) => s.trim()).filter(Boolean);
        break;
    }
  }
  return { value, errors };
}

export const ContactCategoryInputSchema = z.object({
  category_key: z.string().min(1),
  attributes: z.record(z.unknown()).default({}),
  since: z.string().nullable().optional(),
  primary: z.boolean().optional(),
});
export type ContactCategoryInput = z.infer<typeof ContactCategoryInputSchema>;

const emailField = z
  .string()
  .max(255)
  .refine((s) => s === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s), 'Ungültige E-Mail-Adresse')
  .nullable()
  .optional();

export const ContactBaseSchema = z.object({
  kind: z.enum(CONTACT_KINDS),
  organisation_id: z.number().int().nullable().optional(),
  salutation: z.enum(SALUTATIONS).nullable().optional(),
  title: z.string().max(30).nullable().optional(),
  first_name: z.string().max(100).nullable().optional(),
  last_name: z.string().max(100).nullable().optional(),
  org_name: z.string().max(200).nullable().optional(),
  position: z.string().max(120).nullable().optional(),
  street: z.string().max(200).nullable().optional(),
  zip: z.string().max(10).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  state_code: z.string().max(5).nullable().optional(),
  region: z.string().max(100).nullable().optional(),
  phone: z.string().max(50).nullable().optional(),
  mobile: z.string().max(50).nullable().optional(),
  email: emailField,
  website: z.string().max(255).nullable().optional(),
  notes: z.string().nullable().optional(),
  external_ids: z.record(z.string()).nullable().optional(),
  sharepoint_url: z.string().max(1000).nullable().optional(),
  is_placeholder: z.boolean().optional(),
  review_needed: z.boolean().optional(),
});

export const ContactCreateSchema = ContactBaseSchema.extend({
  categories: z.array(ContactCategoryInputSchema).min(1, 'Mindestens eine Kategorie'),
  rating: z
    .object({ grade: RatingGradeSchema, reason: z.string().default('') })
    .nullable()
    .optional(),
}).superRefine((c, ctx) => {
  if (c.kind === 'person' && !c.last_name?.trim())
    ctx.addIssue({ code: 'custom', path: ['last_name'], message: 'Nachname ist Pflicht' });
  if (c.kind === 'organisation' && !c.org_name?.trim())
    ctx.addIssue({ code: 'custom', path: ['org_name'], message: 'Name der Organisation ist Pflicht' });
});
export type ContactCreate = z.infer<typeof ContactCreateSchema>;

export const ContactPatchSchema = ContactBaseSchema.partial().extend({
  updated_at: z.string().optional(),
  categories: z.array(ContactCategoryInputSchema).min(1).optional(),
});
export type ContactPatch = z.infer<typeof ContactPatchSchema>;

export const RatingInputSchema = z.object({
  grade: RatingGradeSchema,
  reason: z.string().max(2000).default(''),
  action_id: z.number().int().nullable().optional(),
});
export type RatingInput = z.infer<typeof RatingInputSchema>;

export const PRICE_SOURCES = ['protocol', 'contract', 'invoice', 'manual'] as const;
export const PriceInputSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  season_code: z.enum(['F', 'S', 'H']).nullable().optional(),
  price_per_tree: z.number().min(0).max(9999),
  action_id: z.number().int().nullable().optional(),
  source: z.enum(PRICE_SOURCES).default('manual'),
  note: z.string().max(1000).nullable().optional(),
});
export type PriceInput = z.infer<typeof PriceInputSchema>;

export const MergeInputSchema = z.object({
  target_id: z.number().int(),
  source_id: z.number().int(),
});

export interface ContactCategoryDto {
  category_key: string;
  name: string;
  attributes: Record<string, unknown>;
  since: string | null;
  primary: boolean;
}
export interface ContactPriceDto {
  id: number;
  year: number;
  season_code: 'F' | 'S' | 'H' | null;
  price_per_tree: number;
  action_id: number | null;
  action_code?: string | null;
  source: (typeof PRICE_SOURCES)[number];
  note: string | null;
  created_at: string;
}
export interface ContactRatingDto {
  id: number;
  grade: RatingGrade;
  reason: string;
  rated_at: string;
  rated_by: number | null;
  rated_by_name: string | null;
  action_id: number | null;
}
export interface ContactListItem {
  id: number;
  kind: ContactKind;
  display_name: string;
  org_name: string | null;
  organisation_name: string | null;
  organisation_id: number | null;
  position: string | null;
  city: string | null;
  state_code: string | null;
  state_label: string | null;
  region: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  rating_grade: RatingGrade | null;
  rating_reason: string | null;
  rated_at: string | null;
  categories: string[];
  attributes: Record<string, Record<string, unknown>>;
  last_price: { price: number; year: number } | null;
  contract_year: number | null;
  action_count: number;
  last_action_at: string | null;
  /** nächste Aktion nach heute */
  next_action_at?: string | null;
  /** Summen aus den Aktionen als Spendenpartner */
  partner_trees?: number;
  partner_donation?: number;
  is_placeholder: boolean;
  review_needed: boolean;
  archived_at: string | null;
  updated_at: string;
}
export interface ContactSummary {
  last_price: { price: number; year: number; season_code: string | null } | null;
  contract: { exists: boolean; year: number | null };
  confirmations: { done: number; total: number; missing: { id: number; label: string }[] };
  actions: { count: number; hectares: number; trees: number; participants: number };
  areas_count: number;
}
export interface ContactActionRow {
  id: number;
  code: string;
  label: string;
  date: string | null;
  type: string | null;
  area_name: string | null;
  area_id: number | null;
  trees: number | null;
  hectares: number | null;
  status: string;
  status_label: string;
  after: { kind: 'secured' | 'monitoring' | 'confirmation_missing' | 'done'; label: string } | null;
  role: string;
}
export interface ContactAreaRow {
  id: number;
  name: string;
  state_code: string | null;
  region: string | null;
  hectares: number | null;
  status: string;
  forester_name: string | null;
}
export interface ContactDetail extends ContactListItem {
  salutation: string | null;
  title: string | null;
  first_name: string | null;
  last_name: string | null;
  street: string | null;
  zip: string | null;
  website: string | null;
  notes: string | null;
  external_ids: Record<string, string> | null;
  sharepoint_url: string | null;
  merged_into: number | null;
  category_details: ContactCategoryDto[];
  organisation: { id: number; display_name: string; street: string | null; zip: string | null; city: string | null; region: string | null; state_code: string | null } | null;
  members: { id: number; display_name: string; position: string | null; rating_grade: RatingGrade | null }[];
  prices: ContactPriceDto[];
  ratings: ContactRatingDto[];
  actions: ContactActionRow[];
  areas: ContactAreaRow[];
  summary: ContactSummary;
  nearby: Record<string, { id: number; display_name: string; rating_grade: RatingGrade | null; city: string | null }[]>;
  created_at: string;
}
export interface ContactPickItem {
  id: number;
  display_name: string;
  sub: string | null;
  rating_grade: RatingGrade | null;
  rating_reason: string | null;
  city: string | null;
  region: string | null;
  last_price: { price: number; year: number } | null;
  categories: string[];
}
export interface DuplicateCandidate {
  a: { id: number; display_name: string; email: string | null; mobile: string | null };
  b: { id: number; display_name: string; email: string | null; mobile: string | null };
  reason: 'name' | 'email' | 'mobile';
}
